/**
 * `aria-rowcount` for `mode="paged"` — the SERVER total, never the loaded page.
 *
 *   MUI   1 + pinned + rows IN STATE   ──▶     26   "row 4 of 26"
 *   truth 1 + pinned + rows ON SERVER  ──▶  8,001   "row 4 of 8,001"
 *
 * MUI derives `aria-rowcount` from `gridExpandedRowCountSelector`
 * (`useGridAriaAttributes.js:19`), which under `paginationMode="server"` holds
 * exactly ONE page. So a screen reader announces a table of 25 rows over a
 * dataset of 8,000, and nothing in the accessibility tree says the other 7,975
 * exist. The `rowCount` prop DOES carry the server total, but MUI spends it on
 * the page count and the footer only.
 *
 * WAI-ARIA 1.2 `aria-rowcount`: the value counts every row of the FULL table
 * — header rows included, which is why `aria-rowindex` on the header row is 1
 * — and is exactly `-1` when the total is unknown. A paged grid is in the
 * unknown case on its first load, and on any page where the server omits the
 * count before one has been cached.
 */
import type { DataGridPremiumProps, GridColumnNode } from '@mui/x-data-grid-premium';

/** WAI-ARIA 1.2: the one legal value for "the total is not known". */
export const ARIA_ROW_COUNT_UNKNOWN = -1;

/** The column-header row itself — MUI counts it, and `aria-rowindex` 1 is it. */
const HEADER_ROW = 1;

/**
 * MUI's `headerGroupingMaxDepth`: the deepest chain of groups above a column,
 * which is how many EXTRA header rows the grid paints. Zero without a grouping
 * model, which is every grid on the platform today.
 */
const columnGroupingDepth = (nodes: readonly GridColumnNode[] | undefined): number =>
  nodes?.reduce(
    (deepest, node) => Math.max(deepest, 'children' in node ? 1 + columnGroupingDepth(node.children) : 0),
    0
  ) ?? 0;

type PagedAriaRowCountInput = {
  /** The server's total for the whole result set — NOT the rows on this page. */
  totalRowCount?: number | null;
  pinnedRows?: DataGridPremiumProps['pinnedRows'];
  columnGroupingModel?: DataGridPremiumProps['columnGroupingModel'];
};

/**
 * Rows the grid claims to have: every server row, plus the header row(s) and any
 * pinned rows, which are real `role="row"` elements the grid renders on top of
 * the page window. `-1` while the total is unknown.
 */
export function pagedAriaRowCount({ totalRowCount, pinnedRows, columnGroupingModel }: PagedAriaRowCountInput): number {
  // `-1` is also how MUI's own `rowCount` prop spells "unknown", so it arrives here.
  if (typeof totalRowCount !== 'number' || totalRowCount < 0) {
    return ARIA_ROW_COUNT_UNKNOWN;
  }

  const pinnedRowCount = (pinnedRows?.top?.length ?? 0) + (pinnedRows?.bottom?.length ?? 0);

  return HEADER_ROW + columnGroupingDepth(columnGroupingModel) + pinnedRowCount + totalRowCount;
}
